import React, { memo } from 'react';

const VIDEO_URL_RE = /\.(mp4|webm)(\?|#|$)/i;

export function isQuestionVideoMediaUrl(url: string | undefined | null): boolean {
  return typeof url === 'string' && VIDEO_URL_RE.test(url);
}

interface QuestionImageOrVideoProps {
  url: string;
  alt: string;
  className?: string;
  loading?: 'eager' | 'lazy';
  onImgError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}

/**
 * Renders image URLs as <img>; video URLs (.mp4 / .webm) as autoplaying muted <video>.
 * Using <img> for video URLs fails silently in browsers — this avoids that class of bug.
 */
export const QuestionImageOrVideo = memo(function QuestionImageOrVideo({
  url,
  alt,
  className,
  loading = 'eager',
  onImgError,
}: QuestionImageOrVideoProps) {
  if (isQuestionVideoMediaUrl(url)) {
    return (
      <video
        className={className}
        src={url}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-label={alt}
        onError={(e) => {
          (e.currentTarget as HTMLVideoElement).style.display = 'none';
        }}
      />
    );
  }

  return (
    <img
      src={url}
      alt={alt}
      className={className}
      loading={loading}
      fetchPriority={loading === 'eager' ? 'high' : 'auto'}
      decoding="async"
      onError={
        onImgError ??
        ((e) => {
          const target = e.currentTarget;
          target.style.display = 'none';
        })
      }
    />
  );
});

QuestionImageOrVideo.displayName = 'QuestionImageOrVideo';
